/**
 * 生成設定パネル（Phase 42）。
 * 2 タブ構成：
 *  - 基本設定: A 配信設定 / B ブランド・コピー / C 生成タイプ・素材
 *  - デザイン設定: D ムード・スタイル / E レイアウト・仕上げ / F 生成設定
 * 生成タイプ（01/02/03）の明示選択に応じて素材スロットを出し分ける。
 */
import {
  AD_MOOD_OPTIONS,
  AD_SEASON_OPTIONS,
  AD_SIZE_PRESETS,
  type AdAudience,
  type AdImageConfig,
  type AdMediaKind,
  type AdMoodKey,
  type AdPlatform,
  type AdProductType,
  type AdSeasonKey,
  type AdSizePresetId,
} from '@goenhub/shared';
import { ChevronDown, Loader2, Sparkles, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';

import { AdImagePickerDialog } from './AdImagePickerDialog';

interface Props {
  config: AdImageConfig;
  onChange: (c: AdImageConfig) => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

const AUDIENCE_OPTIONS: Array<{ id: AdAudience; label: string }> = [
  { id: 'btob', label: 'BtoB' },
  { id: 'btoc', label: 'BtoC' },
];

const PRODUCT_TYPE_OPTIONS: Array<{ id: AdProductType; label: string }> = [
  { id: 'product', label: '物理製品' },
  { id: 'digital', label: 'デジタル製品' },
  { id: 'service', label: 'サービス' },
];

const MEDIA_KIND_OPTIONS: Array<{ id: AdMediaKind; label: string }> = [
  { id: 'image', label: '画像（1 枚）' },
  { id: 'video_slides', label: '動画スライド（複数枚）' },
];

const PLATFORM_OPTIONS: Array<{ id: AdPlatform; label: string }> = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'instagram_story', label: 'Instagram ストーリーズ' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'youtube_shorts', label: 'YouTube Shorts' },
  { id: 'line', label: 'LINE' },
  { id: 'banner', label: '汎用バナー' },
];

type AdType = NonNullable<AdImageConfig['adType']>;

const AD_TYPE_OPTIONS: Array<{ id: AdType; label: string; desc: string }> = [
  { id: '01', label: '① 主題合成型', desc: '用意した背景に主題（商品・人物）を合成します。' },
  { id: '02', label: '② 主題差し替え型', desc: '既存のベース広告の主題だけを別のものに差し替えます。' },
  {
    id: '03',
    label: '③ 背景生成＋合成型（推奨）',
    desc: '背景を AI で生成し、必要なら主題を合成します。',
  },
];

const CONTRAST_OPTIONS: Array<{ id: NonNullable<AdImageConfig['contrastMode']>; label: string }> = [
  { id: 'auto', label: 'AI 自動（推奨）' },
  { id: 'dark_on_light', label: '明背景 + 黒文字' },
  { id: 'white_on_dark', label: '暗背景 + 白文字' },
  { id: 'tinted_overlay', label: '半透明オーバーレイ' },
];

/** AdImagePickerDialog で URL をセットする対象フィールド */
type ImageField =
  | 'subjectImageUrl'
  | 'backgroundImageUrl'
  | 'baseImageUrl'
  | 'replacementSubjectUrl';

export function GenerateTab({ config, onChange, onGenerate, isGenerating }: Props) {
  const sizesForPlatform = useMemo(
    () => AD_SIZE_PRESETS.filter((p) => p.platform === config.platform),
    [config.platform],
  );

  const setField = <K extends keyof AdImageConfig>(key: K, value: AdImageConfig[K]) => {
    onChange({ ...config, [key]: value });
  };

  const [tab, setTab] = useState<'basic' | 'design'>('basic');
  const [open, setOpen] = useState<Record<string, boolean>>({
    A: true,
    B: true,
    C: true,
    D: true,
    E: false,
    F: true,
  });
  const toggle = (id: string) => setOpen((s) => ({ ...s, [id]: !s[id] }));

  /** どのフィールドに対して画像ピッカーを開いているか（null = 閉） */
  const [pickerField, setPickerField] = useState<ImageField | null>(null);

  const adType: AdType = config.adType ?? '03';

  return (
    <div className="relative">
      {/* タブ */}
      <div className="sticky top-0 z-10 flex gap-1 px-1 pb-3 bg-[var(--bg-elevated)]">
        {([
          { id: 'basic' as const, label: '基本設定' },
          { id: 'design' as const, label: 'デザイン設定' },
        ]).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex-1 px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
              tab === t.id
                ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-2 px-1 pb-4">
        {tab === 'basic' ? (
          <>
            {/* A 配信設定 */}
            <Accordion id="A" title="A · 配信設定" open={open.A} onToggle={toggle}>
              <Block title="ターゲット">
                <ChipGroup
                  options={AUDIENCE_OPTIONS}
                  value={config.audience}
                  onChange={(v) => setField('audience', v)}
                />
              </Block>
              <Block title="商材タイプ">
                <ChipGroup
                  options={PRODUCT_TYPE_OPTIONS}
                  value={config.productType}
                  onChange={(v) => setField('productType', v)}
                />
              </Block>
              <Block title="出力種別">
                <ChipGroup
                  options={MEDIA_KIND_OPTIONS}
                  value={config.mediaKind}
                  onChange={(v) => setField('mediaKind', v)}
                />
                {config.mediaKind === 'video_slides' && (
                  <div className="mt-2">
                    <label className="text-xs text-[var(--text-muted)] block mb-1">
                      スライド枚数
                    </label>
                    <Input
                      type="number"
                      min={2}
                      max={10}
                      value={config.slideCount ?? 3}
                      onChange={(e) =>
                        setField(
                          'slideCount',
                          Math.max(2, Math.min(10, Number(e.target.value) || 3)),
                        )
                      }
                    />
                  </div>
                )}
              </Block>
              <Block title="媒体">
                <ChipGroup
                  options={PLATFORM_OPTIONS}
                  value={config.platform}
                  onChange={(v) => {
                    const next = AD_SIZE_PRESETS.find((p) => p.platform === v);
                    onChange({
                      ...config,
                      platform: v,
                      sizePresetId: next ? next.id : config.sizePresetId,
                    });
                  }}
                />
              </Block>
              <Block title="サイズプリセット">
                <div className="space-y-1">
                  {sizesForPlatform.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setField('sizePresetId', p.id as AdSizePresetId)}
                      className={`w-full text-left px-3 py-2 rounded-sm border text-xs ${
                        config.sizePresetId === p.id
                          ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                          : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      <div className="font-medium text-[var(--text-primary)]">{p.label}</div>
                      <div className="text-[var(--text-muted)] text-[11px]">
                        {p.width}×{p.height} px · {p.aspectRatio}
                        {p.note ? ` · ${p.note}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              </Block>
            </Accordion>

            {/* B ブランド・コピー */}
            <Accordion id="B" title="B · ブランド・コピー" open={open.B} onToggle={toggle}>
              <div className="space-y-2">
                <Input
                  placeholder="ブランド名"
                  value={config.brand ?? ''}
                  onChange={(e) => setField('brand', e.target.value)}
                />
                <Input
                  placeholder="商材名"
                  value={config.productName ?? ''}
                  onChange={(e) => setField('productName', e.target.value)}
                />
                <Textarea
                  placeholder="キャッチコピーの方向性（任意）"
                  value={config.message ?? ''}
                  onChange={(e) => setField('message', e.target.value)}
                  rows={3}
                />
              </div>
              <Block title="キャンペーン情報（任意）">
                <Textarea
                  placeholder="例: 期間限定 12/24 まで / 早期申込み 30% OFF / 先着 100 名様プレゼント"
                  value={config.campaignInfo ?? ''}
                  onChange={(e) => setField('campaignInfo', e.target.value)}
                  rows={2}
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1">
                  セール時期や特典を記入すると、AI が「期間限定」バッジや星型ベクターで強調します。
                </p>
              </Block>
            </Accordion>

            {/* C 生成タイプ・素材 */}
            <Accordion id="C" title="C · 生成タイプ・素材" open={open.C} onToggle={toggle}>
              <Block title="生成タイプ">
                <div className="space-y-1.5">
                  {AD_TYPE_OPTIONS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setField('adType', o.id)}
                      className={`w-full text-left px-3 py-2 rounded-sm text-xs border transition-colors ${
                        adType === o.id
                          ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                          : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      <div className="font-medium text-[var(--text-primary)]">{o.label}</div>
                      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{o.desc}</div>
                    </button>
                  ))}
                </div>
              </Block>

              {/* タイプ別 素材スロット */}
              {adType === '01' && (
                <>
                  <Block title="背景画像（必須）">
                    <ImageSlot
                      url={config.backgroundImageUrl ?? null}
                      onPick={() => setPickerField('backgroundImageUrl')}
                      onClear={() => setField('backgroundImageUrl', null)}
                    />
                  </Block>
                  <Block title="主題画像（必須）— 商品 / 人物の透明 PNG 推奨">
                    <ImageSlot
                      url={config.subjectImageUrl ?? null}
                      onPick={() => setPickerField('subjectImageUrl')}
                      onClear={() => setField('subjectImageUrl', null)}
                    />
                  </Block>
                  <Block title="合成指示（任意）">
                    <Textarea
                      placeholder="例: 主題を中央やや右に配置し、背景の光源と影を馴染ませる"
                      value={config.compositePrompt ?? ''}
                      onChange={(e) => setField('compositePrompt', e.target.value)}
                      rows={2}
                    />
                  </Block>
                </>
              )}

              {adType === '02' && (
                <>
                  <Block title="ベース画像（必須）— 差し替え対象の既存広告">
                    <ImageSlot
                      url={config.baseImageUrl ?? null}
                      onPick={() => setPickerField('baseImageUrl')}
                      onClear={() => setField('baseImageUrl', null)}
                    />
                  </Block>
                  <Block title="差し替え主題画像（必須）">
                    <ImageSlot
                      url={config.replacementSubjectUrl ?? null}
                      onPick={() => setPickerField('replacementSubjectUrl')}
                      onClear={() => setField('replacementSubjectUrl', null)}
                    />
                  </Block>
                  <Block title="差し替え指示（任意）">
                    <Textarea
                      placeholder="例: 元の商品と同じ位置・サイズで新しい主題に置き換える"
                      value={config.replacePrompt ?? ''}
                      onChange={(e) => setField('replacePrompt', e.target.value)}
                      rows={2}
                    />
                  </Block>
                </>
              )}

              {adType === '03' && (
                <>
                  <Block title="背景生成プロンプト">
                    <Textarea
                      placeholder="例: 柔らかな自然光のスタジオ背景、ベージュとグリーンの観葉植物、余白多め"
                      value={config.backgroundGenPrompt ?? ''}
                      onChange={(e) => setField('backgroundGenPrompt', e.target.value)}
                      rows={3}
                    />
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">
                      AI が生成する背景のムード・配色・構図を記述します。文字やロゴは含めません。
                    </p>
                  </Block>
                  <div className="grid grid-cols-2 gap-2">
                    <Block title="ムードカラー（任意）">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.moodColor ?? '#cda87a'}
                          onChange={(e) => setField('moodColor', e.target.value)}
                          className="h-8 w-10 rounded-sm border border-[var(--border)] bg-transparent p-0.5"
                        />
                        {config.moodColor && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setField('moodColor', null)}
                          >
                            解除
                          </Button>
                        )}
                      </div>
                    </Block>
                    <Block title="デザインキー（任意）">
                      <Input
                        placeholder="例: scandinavian"
                        value={config.designKey ?? ''}
                        onChange={(e) => setField('designKey', e.target.value || null)}
                      />
                    </Block>
                  </div>
                  <Block title="主題画像（任意）— 合成する場合のみ">
                    <ImageSlot
                      url={config.subjectImageUrl ?? null}
                      onPick={() => setPickerField('subjectImageUrl')}
                      onClear={() => setField('subjectImageUrl', null)}
                    />
                  </Block>
                  {config.subjectImageUrl && (
                    <Block title="合成指示（任意）">
                      <Textarea
                        placeholder="例: 主題を中央に配置し、生成背景の光と影に馴染ませる"
                        value={config.compositePrompt ?? ''}
                        onChange={(e) => setField('compositePrompt', e.target.value)}
                        rows={2}
                      />
                    </Block>
                  )}
                </>
              )}
            </Accordion>
          </>
        ) : (
          <>
            {/* D ムード・スタイル */}
            <Accordion id="D" title="D · ムード・スタイル" open={open.D} onToggle={toggle}>
              <Block title="デザインムード（複数選択可）">
                <MoodSelector
                  selected={config.moodKeys ?? []}
                  onChange={(keys) => setField('moodKeys', keys)}
                />
              </Block>
              <Block title="季節感（任意）">
                <SeasonSelector
                  selected={config.seasonKey ?? null}
                  onChange={(k) => setField('seasonKey', k)}
                />
              </Block>
              <Block title="自由トーン入力（任意・上級者向け）">
                <Input
                  placeholder="例: vibrant + editorial、scandinavian, minimal warm"
                  value={config.tone ?? ''}
                  onChange={(e) => setField('tone', e.target.value)}
                />
              </Block>
            </Accordion>

            {/* E レイアウト・仕上げ */}
            <Accordion id="E" title="E · レイアウト・仕上げ" open={open.E} onToggle={toggle}>
              <Block title="テキストコントラスト方式">
                <div className="grid grid-cols-2 gap-1">
                  {CONTRAST_OPTIONS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setField('contrastMode', o.id)}
                      className={`px-2 py-1.5 rounded-sm text-[11px] border transition-colors ${
                        (config.contrastMode ?? 'auto') === o.id
                          ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                          : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-[var(--text-muted)] mt-1">
                  通常は AI 自動で可読性を担保します。手動で上書きしたい場合のみ指定します。
                </p>
              </Block>
            </Accordion>

            {/* F 生成設定 */}
            <Accordion id="F" title="F · 生成設定" open={open.F} onToggle={toggle}>
              {adType === '03' ? (
                <Block title="画像生成方式">
                  <div className="space-y-1.5">
                    {[
                      {
                        id: 'verbal' as const,
                        label: '軽量（言語化方式）',
                        desc: '低コスト・高速。背景を生成し主題はレイヤー合成。被写体混入リスクなし。',
                      },
                      {
                        id: 'three_step' as const,
                        label: '高品質（3 段階生成）',
                        desc: '背景生成 → 主題合成 → 最終仕上げの 3 段階。スタイル忠実度が高い。',
                      },
                    ].map((o) => {
                      const current = config.generationMode ?? 'verbal';
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => setField('generationMode', o.id)}
                          className={`w-full text-left px-3 py-2 rounded-sm text-xs border transition-colors ${
                            current === o.id
                              ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                              : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                          }`}
                        >
                          <div className="font-medium text-[var(--text-primary)]">{o.label}</div>
                          <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                            {o.desc}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Block>
              ) : (
                <p className="text-[11px] text-[var(--text-muted)] px-1 py-2">
                  画像生成方式はタイプ③（背景生成＋合成型）でのみ選択できます。
                </p>
              )}
            </Accordion>
          </>
        )}
      </div>

      {/* 共有画像ピッカー */}
      <AdImagePickerDialog
        open={pickerField !== null}
        onOpenChange={(o) => {
          if (!o) setPickerField(null);
        }}
        onSelect={(url) => {
          if (pickerField) setField(pickerField, url);
          setPickerField(null);
        }}
      />

      {/* 生成バー（sticky） */}
      <div className="sticky bottom-0 border-t border-[var(--border)] bg-[var(--bg-elevated)] px-1 py-3">
        <Button type="button" onClick={onGenerate} disabled={isGenerating} className="w-full">
          {isGenerating ? (
            <Loader2 className="size-4 mr-1 animate-spin" />
          ) : (
            <Sparkles className="size-4 mr-1" />
          )}
          生成する
        </Button>
      </div>
    </div>
  );
}

function Accordion({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  open: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border border-[var(--border)] overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-primary)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]"
      >
        <span>{title}</span>
        <ChevronDown
          className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="space-y-3 p-3">{children}</div>}
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-2">
        {title}
      </h4>
      {children}
    </div>
  );
}

function ImageSlot({
  url,
  onPick,
  onClear,
}: {
  url: string | null;
  onPick: () => void;
  onClear: () => void;
}) {
  if (url) {
    return (
      <div className="space-y-2">
        <img
          src={url}
          alt="selected"
          className="w-full max-h-32 object-contain border border-[var(--border)] rounded-sm bg-[var(--bg-elevated)]"
        />
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={onPick}>
            変更
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear}>
            削除
          </Button>
        </div>
      </div>
    );
  }
  return (
    <Button size="sm" variant="ghost" onClick={onPick}>
      画像を選択
    </Button>
  );
}

interface ChipOption<T extends string> {
  id: T;
  label: string;
}

function ChipGroup<T extends string>({
  options,
  value,
  onChange,
}: {
  options: ChipOption<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`px-2.5 py-1 rounded-sm text-xs border transition-colors ${
            value === o.id
              ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
              : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MoodSelector({
  selected,
  onChange,
}: {
  selected: AdMoodKey[];
  onChange: (next: AdMoodKey[]) => void;
}) {
  const toggle = (k: AdMoodKey) => {
    if (selected.includes(k)) onChange(selected.filter((s) => s !== k));
    else onChange([...selected, k]);
  };
  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 pb-1 border-b border-[var(--border)]">
          {selected.map((k) => {
            const m = AD_MOOD_OPTIONS.find((o) => o.key === k);
            if (!m) return null;
            return (
              <span
                key={k}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-[var(--accent)]/15 text-[var(--text-primary)] border border-[var(--accent)]"
              >
                {m.label}
                <button
                  type="button"
                  onClick={() => toggle(k)}
                  className="hover:text-[var(--accent-red)]"
                  aria-label={`${m.label} を解除`}
                >
                  <X className="size-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {AD_MOOD_OPTIONS.map((m) => {
          const isOn = selected.includes(m.key);
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => toggle(m.key)}
              className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${
                isOn
                  ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                  : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
              }`}
              title={m.englishHint}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SeasonSelector({
  selected,
  onChange,
}: {
  selected: AdSeasonKey | null;
  onChange: (next: AdSeasonKey | null) => void;
}) {
  return (
    <div className="space-y-2">
      {selected && (
        <div className="flex flex-wrap gap-1 pb-1 border-b border-[var(--border)]">
          {(() => {
            const s = AD_SEASON_OPTIONS.find((o) => o.key === selected);
            if (!s) return null;
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-[var(--accent)]/15 text-[var(--text-primary)] border border-[var(--accent)]">
                {s.label}
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="hover:text-[var(--accent-red)]"
                >
                  <X className="size-2.5" />
                </button>
              </span>
            );
          })()}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${
            selected === null
              ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
              : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          指定なし
        </button>
        {AD_SEASON_OPTIONS.map((s) => {
          const isOn = selected === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${
                isOn
                  ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                  : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
              }`}
              title={s.englishHint}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
